'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Globe, WarningCircle, Cursor, StopCircle } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';

/**
 * Live-view v2 canvas client (browser-v2). Connects to the in-sandbox screencast
 * relay (scripts/liveview.py) over the Daytona preview ws URL and renders JPEG
 * frames onto a canvas. "Take control" forwards mouse + keyboard to the sandbox
 * browser via CDP Input.dispatch* (relay protocol). Vercel never touches a frame.
 *
 * Wire format (mirrors scripts/liveview.py):
 *   binary frame = [uint32 BE headerLen][JSON header][raw JPEG bytes]
 *   header = { seq, w: deviceWidthCssPx, h: deviceHeightCssPx, ts }
 *   text control = { type: 'hello' | 'cdp' | 'bye', ... }
 *   client -> server = { type: 'mouse'|'key', params: <CDP Input params> }
 */

type ConnState = 'connecting' | 'live' | 'ended';

interface FrameLayout {
  offsetX: number;
  offsetY: number;
  drawW: number;
  drawH: number;
  frameW: number;
  frameH: number;
}

function getLiveViewUrl(output: unknown): string | undefined {
  if (!output || typeof output !== 'object') return undefined;
  const out = output as { liveView?: { url?: unknown }; data?: { liveView?: { url?: unknown } } };
  const url = out.liveView?.url ?? out.data?.liveView?.url;
  return typeof url === 'string' && url.startsWith('ws') ? url : undefined;
}

function modifiersFrom(e: { altKey: boolean; ctrlKey: boolean; metaKey: boolean; shiftKey: boolean }) {
  return (e.altKey ? 1 : 0) | (e.ctrlKey ? 2 : 0) | (e.metaKey ? 4 : 0) | (e.shiftKey ? 8 : 0);
}

export function BrowserLiveView({
  output,
  displayContext = 'inline',
}: {
  input?: Record<string, unknown>;
  output?: unknown;
  displayContext?: 'inline' | 'artifact';
}) {
  const url = useMemo(() => getLiveViewUrl(output), [output]);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const layoutRef = useRef<FrameLayout | null>(null);
  const reconnectedRef = useRef(false);
  const stoppedRef = useRef(false);
  const lastMoveRef = useRef(0);

  const [state, setState] = useState<ConnState>('connecting');
  const [takeover, setTakeover] = useState(false);
  const [fps, setFps] = useState(0);
  const [frameKb, setFrameKb] = useState(0);
  const [dims, setDims] = useState<{ w: number; h: number } | null>(null);

  // Rolling FPS / size stats.
  const statRef = useRef<{ frames: number; bytes: number; since: number }>({ frames: 0, bytes: 0, since: 0 });

  const drawFrame = useCallback(async (jpeg: Uint8Array, frameW: number, frameH: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let bitmap: ImageBitmap;
    try {
      // Copy into a fresh ArrayBuffer so the Blob owns contiguous bytes.
      bitmap = await createImageBitmap(new Blob([jpeg.slice()], { type: 'image/jpeg' }));
    } catch {
      return;
    }
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    const cssW = canvas.clientWidth || frameW;
    const cssH = canvas.clientHeight || frameH;
    if (canvas.width !== Math.round(cssW * dpr) || canvas.height !== Math.round(cssH * dpr)) {
      canvas.width = Math.round(cssW * dpr);
      canvas.height = Math.round(cssH * dpr);
    }
    // Fit-contain (letterbox) in device pixels.
    const scale = Math.min(canvas.width / bitmap.width, canvas.height / bitmap.height);
    const drawW = bitmap.width * scale;
    const drawH = bitmap.height * scale;
    const offsetX = (canvas.width - drawW) / 2;
    const offsetY = (canvas.height - drawH) / 2;
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(bitmap, offsetX, offsetY, drawW, drawH);
    bitmap.close();
    // Store CSS-space layout for coordinate mapping (canvas -> page).
    layoutRef.current = {
      offsetX: offsetX / dpr,
      offsetY: offsetY / dpr,
      drawW: drawW / dpr,
      drawH: drawH / dpr,
      frameW: frameW || bitmap.width,
      frameH: frameH || bitmap.height,
    };
  }, []);

  const connect = useCallback(() => {
    if (!url || stoppedRef.current) return;
    setState('connecting');
    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch {
      setState('ended');
      return;
    }
    ws.binaryType = 'arraybuffer';
    wsRef.current = ws;
    statRef.current = { frames: 0, bytes: 0, since: performance.now() };

    ws.onopen = () => {
      setState('live');
    };
    ws.onmessage = (ev) => {
      if (typeof ev.data === 'string') {
        // Text control message — currently informational only.
        return;
      }
      const buf = new Uint8Array(ev.data as ArrayBuffer);
      if (buf.byteLength < 4) return;
      const view = new DataView(buf.buffer, buf.byteOffset, buf.byteLength);
      const headerLen = view.getUint32(0);
      const headerBytes = buf.subarray(4, 4 + headerLen);
      let frameW = 0;
      let frameH = 0;
      try {
        const header = JSON.parse(new TextDecoder().decode(headerBytes));
        frameW = Number(header.w) || 0;
        frameH = Number(header.h) || 0;
      } catch {
        /* tolerate a malformed header */
      }
      const jpeg = buf.subarray(4 + headerLen);
      if (frameW && frameH) setDims((prev) => (prev?.w === frameW && prev?.h === frameH ? prev : { w: frameW, h: frameH }));
      void drawFrame(jpeg, frameW, frameH);
      // Stats.
      const s = statRef.current;
      s.frames += 1;
      s.bytes += jpeg.byteLength;
      const elapsed = performance.now() - s.since;
      if (elapsed >= 1000) {
        setFps(Math.round((s.frames / elapsed) * 1000));
        setFrameKb(Math.round((s.bytes / s.frames / 1024) * 10) / 10);
        statRef.current = { frames: 0, bytes: 0, since: performance.now() };
      }
    };
    const onClose = () => {
      wsRef.current = null;
      if (stoppedRef.current) {
        setState('ended');
        return;
      }
      if (!reconnectedRef.current) {
        reconnectedRef.current = true;
        setState('connecting');
        setTimeout(() => connect(), 1000);
      } else {
        setState('ended');
      }
    };
    ws.onclose = onClose;
    ws.onerror = () => {
      try { ws.close(); } catch { /* noop */ }
    };
  }, [url, drawFrame]);

  useEffect(() => {
    stoppedRef.current = false;
    reconnectedRef.current = false;
    connect();
    return () => {
      stoppedRef.current = true;
      try { wsRef.current?.close(); } catch { /* noop */ }
    };
  }, [connect]);

  const stopViewing = useCallback(() => {
    stoppedRef.current = true;
    try { wsRef.current?.close(); } catch { /* noop */ }
    setState('ended');
  }, []);

  // ---- takeover: map canvas events to page coords + forward over ws --------
  const toPageCoords = useCallback((clientX: number, clientY: number) => {
    const canvas = canvasRef.current;
    const layout = layoutRef.current;
    if (!canvas || !layout) return null;
    const rect = canvas.getBoundingClientRect();
    const cx = clientX - rect.left - layout.offsetX;
    const cy = clientY - rect.top - layout.offsetY;
    if (cx < 0 || cy < 0 || cx > layout.drawW || cy > layout.drawH) return null;
    return {
      x: Math.round((cx / layout.drawW) * layout.frameW),
      y: Math.round((cy / layout.drawH) * layout.frameH),
    };
  }, []);

  const sendInput = useCallback((msg: Record<string, unknown>) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(msg));
  }, []);

  const onMouseMove = useCallback((e: React.MouseEvent) => {
    if (!takeover) return;
    const now = performance.now();
    if (now - lastMoveRef.current < 33) return; // ~30/s
    lastMoveRef.current = now;
    const p = toPageCoords(e.clientX, e.clientY);
    if (!p) return;
    sendInput({ type: 'mouse', params: { type: 'mouseMoved', x: p.x, y: p.y, button: 'none', buttons: e.buttons, modifiers: modifiersFrom(e) } });
  }, [takeover, toPageCoords, sendInput]);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    if (!takeover) return;
    e.preventDefault();
    canvasRef.current?.focus();
    const p = toPageCoords(e.clientX, e.clientY);
    if (!p) return;
    sendInput({ type: 'mouse', params: { type: 'mousePressed', x: p.x, y: p.y, button: 'left', buttons: 1, clickCount: e.detail || 1, modifiers: modifiersFrom(e) } });
  }, [takeover, toPageCoords, sendInput]);

  const onMouseUp = useCallback((e: React.MouseEvent) => {
    if (!takeover) return;
    e.preventDefault();
    const p = toPageCoords(e.clientX, e.clientY);
    if (!p) return;
    sendInput({ type: 'mouse', params: { type: 'mouseReleased', x: p.x, y: p.y, button: 'left', buttons: 0, clickCount: e.detail || 1, modifiers: modifiersFrom(e) } });
  }, [takeover, toPageCoords, sendInput]);

  const onWheel = useCallback((e: React.WheelEvent) => {
    if (!takeover) return;
    const p = toPageCoords(e.clientX, e.clientY);
    if (!p) return;
    sendInput({ type: 'mouse', params: { type: 'mouseWheel', x: p.x, y: p.y, deltaX: e.deltaX, deltaY: e.deltaY, modifiers: modifiersFrom(e) } });
  }, [takeover, toPageCoords, sendInput]);

  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (!takeover) return;
    e.preventDefault();
    if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
      sendInput({ type: 'key', params: { type: 'char', text: e.key } });
    } else {
      sendInput({ type: 'key', params: { type: 'keyDown', key: e.key, code: e.code, windowsVirtualKeyCode: e.keyCode, modifiers: modifiersFrom(e) } });
    }
  }, [takeover, sendInput]);

  const onKeyUp = useCallback((e: React.KeyboardEvent) => {
    if (!takeover) return;
    e.preventDefault();
    if (!(e.key.length === 1 && !e.ctrlKey && !e.metaKey)) {
      sendInput({ type: 'key', params: { type: 'keyUp', key: e.key, code: e.code, windowsVirtualKeyCode: e.keyCode, modifiers: modifiersFrom(e) } });
    }
  }, [takeover, sendInput]);

  if (!url) {
    return (
      <div className="rounded-lg border border-amber-500/20 bg-amber-500/[0.04] p-4">
        <div className="flex items-start gap-3">
          <WarningCircle weight="duotone" className="mt-0.5 size-5 text-amber-500" />
          <div className="min-w-0">
            <p className="text-sm font-medium">No live view available</p>
            <p className="mt-1 text-sm text-muted-foreground">
              This browser run did not start a live session (live view is chromium-only and must be requested with liveView:true).
            </p>
          </div>
        </div>
      </div>
    );
  }

  const stateChip = (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2 py-1 text-[10px] font-bold uppercase tracking-[2px]',
        state === 'live' && 'border-emerald-500/30 bg-emerald-500/[0.08] text-emerald-300',
        state === 'connecting' && 'border-amber-500/30 bg-amber-500/[0.08] text-amber-300',
        state === 'ended' && 'border-border/60 bg-muted/30 text-muted-foreground',
      )}
    >
      {state === 'live' && (
        <span className="relative flex size-1.5">
          <span className="absolute inline-flex size-full animate-ping rounded-full bg-emerald-400/70" />
          <span className="relative inline-flex size-1.5 rounded-full bg-emerald-400" />
        </span>
      )}
      {state === 'connecting' ? 'Connecting' : state === 'live' ? 'Live' : 'Ended'}
    </span>
  );

  return (
    <div
      className={cn(
        'flex min-h-0 w-full flex-col overflow-hidden rounded-lg border border-border bg-black',
        displayContext === 'artifact' ? 'h-full' : '',
      )}
    >
      <div className="flex min-h-11 shrink-0 flex-wrap items-center gap-2 border-b border-border/60 bg-background/80 px-3 py-1.5 backdrop-blur-md">
        <div className="flex min-w-0 flex-1 items-center gap-2 rounded-full border border-border/70 bg-muted/30 px-3 py-1.5">
          <Globe weight="duotone" className="size-3.5 shrink-0 text-muted-foreground" />
          <span className="truncate text-xs text-muted-foreground">
            Sandbox browser {dims ? `· ${dims.w}×${dims.h}` : ''}
          </span>
        </div>
        {state === 'live' && (
          <span className="shrink-0 font-mono text-[10px] tabular-nums text-muted-foreground/70">
            {fps} fps · {frameKb} KB
          </span>
        )}
        {stateChip}
        <button
          type="button"
          onClick={() => setTakeover((v) => !v)}
          disabled={state !== 'live'}
          className={cn(
            'inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold transition-colors disabled:opacity-40',
            takeover
              ? 'border-violet-500/40 bg-violet-500/15 text-violet-200'
              : 'border-border/70 bg-background/40 text-muted-foreground hover:bg-muted/60 hover:text-foreground',
          )}
        >
          <Cursor weight="bold" className="size-3.5" />
          {takeover ? 'Controlling' : 'Take control'}
        </button>
        <button
          type="button"
          onClick={stopViewing}
          disabled={state === 'ended'}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-border/70 bg-background/40 px-2.5 py-1 text-[11px] font-semibold text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground disabled:opacity-40"
        >
          <StopCircle weight="bold" className="size-3.5" />
          Stop viewing
        </button>
      </div>

      <div className="relative min-h-0 flex-1">
        <canvas
          ref={canvasRef}
          tabIndex={0}
          onMouseMove={onMouseMove}
          onMouseDown={onMouseDown}
          onMouseUp={onMouseUp}
          onWheel={onWheel}
          onKeyDown={onKeyDown}
          onKeyUp={onKeyUp}
          className={cn(
            'block w-full bg-black outline-none',
            displayContext === 'artifact' ? 'h-full' : 'h-[420px]',
            takeover ? 'cursor-none' : 'cursor-default',
          )}
        />
        {state === 'connecting' && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <span className="rounded-full border border-border/60 bg-background/70 px-3 py-1.5 text-xs text-muted-foreground backdrop-blur">
              Connecting to sandbox browser…
            </span>
          </div>
        )}
        {state === 'ended' && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <span className="rounded-full border border-border/60 bg-background/70 px-3 py-1.5 text-xs text-muted-foreground backdrop-blur">
              Live view ended.
            </span>
          </div>
        )}
        {takeover && state === 'live' && (
          <div className="pointer-events-none absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full border border-violet-500/30 bg-violet-500/15 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-violet-200 backdrop-blur">
            You are controlling this browser
          </div>
        )}
      </div>
    </div>
  );
}
